import {
    CodeBlock, BlockType, SettingType, ValidationMessage, SettingState,
    Define,
} from '@appblocks/codegeneration/types';

import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';

export default class HTTPServer extends BaseFeature {
    constructor() {
        super();
        this.name = 'Web (HTTP) Server';
        this.id = 'httpserver';
        this.icon = 'MdOutlineHttp';
        this.requirements = ['network'];

        this.files = [
            'index.html',
            'libraries/http_server/http_server.tbh',
            'libraries/http_server/http_server.tbs',
        ];
        const settings = [
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'ADVANCED',
                name: 'Advanced',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'ENABLED',
                name: 'HTTP Server Enabled',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'PORT',
                name: 'Port',
                max: 65535,
                settingType: SettingType.NUMBER,
                required: true,
                group: 'GENERAL',
                default: '80',
            },
            {
                id: 'SOCKETS',
                name: 'Number of Sockets',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                default: '3',
            },
            {
                id: 'TXBUF',
                name: 'TX Buffer Pages',
                settingType: SettingType.BUFFER,
                required: true,
                default: '10',
            },
            {
                id: 'RXBUF',
                name: 'RX Buffer Pages',
                settingType: SettingType.BUFFER,
                required: true,
                default: '10',
            },
            {
                id: 'VARBUF',
                name: 'VAR Buffer Pages',
                settingType: SettingType.BUFFER,
                required: true,
                default: '1',
            },
            {
                id: 'ENDPOINTS',
                name: 'Endpoints',
                settingType: SettingType.ARRAY,
                required: true,
                group: 'GENERAL',
                refId: 'http_server_endpoint',
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'DEBUG_PRINT',
                name: 'Debug Print',
                settingType: SettingType.OPTION,
                required: true,
                group: 'ADVANCED',
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getFiles(): string[] {
        if (this.project.runtime === 'micropython') {
            return micropython.getFiles(this);
        }
        if (this.project.runtime === 'zephyr') {
            return zephyr.getFiles(this);
        }
        return this.files;
    }

    getDependencies() {
        const dependencies = [
            'sock',
        ];
        return dependencies;
    }

    getDefines(): Define[] {
        const DEBUG_PRINT = this.getSettingValue('DEBUG_PRINT');
        const SOCKETS = this.getSettingValue('SOCKETS');

        const defines = [];
        if (DEBUG_PRINT === '1') {
            defines.push({
                name: 'HTTP_SERVER_DEBUG_PRINT',
                value: '1',
            });
        }
        defines.push({
            name: 'HTTP_SERVER_COUNT',
            value: SOCKETS,
        });
        return defines;
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.DISABLED;
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            default:
                return [];
        }
    }

    getDetails(): string {
        return `
## HTTP Server
The device contains a built-in HTTP server that can serve static HTML pages and handle HTTP requests. 

Custom endpoints can be created to trigger functions on the device.
        `;
    }
}
